import styles from 'legacy/common/components/form/Form.module.scss';

import Link from 'next/link';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import { getCallingCode, getCountryCode } from 'legacy/common/utils/country';

import FormWrapper from '../FormWrapper';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import PhoneInput from 'legacy/common/components/phone-input/PhoneInput';

type SignUpFormProps = {
  onSubmit: (values: {
    password: string;
    phoneNumber: string;
    email: string;
    countryCode: string;
    callingCode: string;
    passwordConfirmation: string;
  }) => void;
  isLoading: boolean;
  errorMessage: string;
};

const SignUpForm: React.FC<SignUpFormProps> = ({
  errorMessage,
  onSubmit,
  isLoading
}) => {
  const { t } = useTranslation('auth');
  const { query } = useRouter();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const togglePasswordVisibility = () => {
    setIsPasswordVisible((isPasswordVisible) => !isPasswordVisible);
  };

  const [isPasswordConfirmationVisible, setIsPasswordConfirmationVisible] =
    useState(false);
  const togglePasswordConfirmationVisibility = () => {
    setIsPasswordConfirmationVisible(
      (isPasswordConfirmationVisible) => !isPasswordConfirmationVisible
    );
  };

  const currentCountryCode = getCountryCode();
  const currentCallingCode = getCallingCode(currentCountryCode);

  return (
    <FormWrapper title={t('signUpForm.title')}>
      <p className={styles.submitError}>{errorMessage}</p>

      <Formik
        initialValues={{
          password: '',
          phoneNumber: '',
          email: '',
          passwordConfirmation: '',
          countryCode: currentCountryCode,
          callingCode: currentCallingCode
        }}
        onSubmit={onSubmit}
        validateOnMount
        validate={(values) => {
          const errors: Record<string, string> = {};

          if (!values.email) {
            errors.email = t('signUpForm.validation.required');
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
            errors.email = t('signUpForm.validation.invalidEmail');
          }

          if (values.phoneNumber && values.phoneNumber.length < 5) {
            errors.phoneNumber = t('signUpForm.validation.minDigits');
          }

          if (!values.password) {
            errors.password = t('signUpForm.validation.required');
          } else if (values.password.length < 8) {
            errors.password = t('signUpForm.validation.minPasswordLength');
          }

          if (!values.passwordConfirmation) {
            errors.passwordConfirmation = t('signUpForm.validation.required');
          } else if (values.passwordConfirmation !== values.password) {
            errors.passwordConfirmation = t(
              'signUpForm.validation.passwordsMatch'
            );
          }

          return errors;
        }}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          setFieldValue,
          handleBlur,
          handleChange
        }) => (
          <Form>
            <div className={styles.group}>
              <p className={classnames(styles.label, styles.small)}>
                {t('signUpForm.phoneNumberLabel')}
              </p>
              <PhoneInput
                value={values.phoneNumber}
                hasError={errors.phoneNumber && touched.phoneNumber}
                testId="input-phone-number"
                countryCode={values.countryCode}
                onCountryChange={(selected) => {
                  setFieldValue('countryCode', selected.countryCode);
                  setFieldValue('callingCode', selected.value);
                }}
                onChange={handleChange('phoneNumber')}
                onBlur={handleBlur('phoneNumber')}
              />

              {errors.phoneNumber && touched.phoneNumber && (
                <div className={classnames(styles.error, styles.right)}>
                  {errors.phoneNumber}
                </div>
              )}

              <p className={classnames(styles.label, styles.small)}>
                {t('signUpForm.emailLabel')}
              </p>
              <Field
                id="email"
                name="email"
                type="email"
                placeholder={t('signUpForm.placeholders.enterEmail')}
                className={classnames(styles.input, {
                  [styles.errorField]: errors.email && touched.email
                })}
                data-testid="input-email"
              />
              {errors.email && touched.email && (
                <div className={classnames(styles.error, styles.right)}>
                  {errors.email}
                </div>
              )}

              <p className={classnames(styles.label, styles.small)}>
                {t('signUpForm.passwordLabel')}
              </p>
              <div className={styles.relative}>
                <Field
                  id="password"
                  name="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  placeholder={t('signUpForm.placeholders.enterPassword')}
                  className={classnames(styles.input, {
                    [styles.errorField]: errors.password && touched.password
                  })}
                  data-testid="input-password"
                />
                {errors.password && touched.password && (
                  <div className={classnames(styles.error, styles.right)}>
                    {errors.password}
                  </div>
                )}
                <div
                  className={styles.inputIcon}
                  onClick={togglePasswordVisibility}
                >
                  {isPasswordVisible ? (
                    <img src="/img/icons/eye.svg" />
                  ) : (
                    <img src="/img/icons/eye-slash.svg" />
                  )}
                </div>
              </div>

              <p className={classnames(styles.label, styles.small)}>
                {t('signUpForm.confirmPasswordLabel')}
              </p>
              <div className={styles.relative}>
                <Field
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  type={isPasswordConfirmationVisible ? 'text' : 'password'}
                  placeholder={t('signUpForm.placeholders.reenterPassword')}
                  className={classnames(styles.input, {
                    [styles.errorField]:
                      errors.passwordConfirmation &&
                      touched.passwordConfirmation
                  })}
                  data-testid="input-confirm-password"
                />
                {errors.passwordConfirmation &&
                  touched.passwordConfirmation && (
                    <div className={classnames(styles.error, styles.right)}>
                      {errors.passwordConfirmation}
                    </div>
                  )}
                <div
                  className={styles.inputIcon}
                  onClick={togglePasswordConfirmationVisibility}
                >
                  {isPasswordConfirmationVisible ? (
                    <img src="/img/icons/eye.svg" />
                  ) : (
                    <img src="/img/icons/eye-slash.svg" />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Button
                testId="button-submit"
                isSubmit
                disabled={!isValid}
                loading={isLoading}
                type={ButtonType.Primary}
              >
                {t('signUpForm.signUpButton')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      <div className="mt-10 mb-6">
        {t('signUpForm.alreadyHaveAccount')}
        <Link
          href={{
            pathname: '/login',
            query: { ...query }
          }}
          legacyBehavior
        >
          <span
            data-test="button-log-in"
            className="ml-1 text-primary-500 cursor-pointer"
          >
            {t('signUpForm.logInLink')}
          </span>
        </Link>
      </div>
    </FormWrapper>
  );
};

export default SignUpForm;
